import { useEffect, useState } from "react";
import { useForm } from "@formspree/react";
import "./App.css";

const EMAIL = "labcodio@gmail.com";

const technologyGroups = [
  {
    number: "01",
    title: "Web & backend",
    description:
      "From customer-facing products to dependable platforms, engineered around your users and workflows.",
    technologies: [
      "JavaScript",
      "TypeScript",
      "React.js",
      "Next.js",
      "Node.js",
      "Express.js",
      "Fastify",
      "NestJS",
      "REST APIs",
      "GraphQL",
      "Python",
      "FastAPI",
      "Redis",
    ],
  },
  {
    number: "02",
    title: "Mobile",
    description:
      "Connected, intuitive mobile products designed to perform beautifully across devices.",
    technologies: ["React Native", "Expo", "Android", "iOS", "Swift", "Kotlin"],
  },
  {
    number: "03",
    title: "Cloud & data",
    description:
      "Cloud foundations that keep your systems resilient, your data useful, and your operations ready to grow.",
    technologies: [
      "AWS",
      "Azure",
      "Google Cloud",
      "MongoDB",
      "DynamoDB",
      "MySQL",
      "PostgreSQL",
      "Amazon S3",
      "Redis",
    ],
  },
  {
    number: "04",
    title: "DevOps & delivery",
    description:
      "Modern delivery pipelines and infrastructure that help teams ship with confidence.",
    technologies: [
      "Docker",
      "Kubernetes",
      "Terraform",
      "AWS CDK",
      "GitHub Actions",
      "OpenTelemetry",
      "CI/CD",
    ],
  },
  {
    number: "05",
    title: "AI & automation",
    description:
      "Practical AI and thoughtful automation, applied to meaningful business challenges.",
    technologies: [
      "AI-powered features",
      "LLM integration",
      "OpenAI & Anthropic",
      "Hugging Face",
      "LangChain & LangGraph",
      "Retrieval-augmented generation",
      "Pinecone & pgvector",
      "Intelligent assistants",
      "Workflow automation",
    ],
  },
];

const pages = {
  about: {
    eyebrow: "About Work Bridge India",
    title: "A technology partner for your next chapter.",
    description:
      "We bring product thinking, design, and engineering together to help ambitious teams turn complex ideas into useful, dependable digital products.",
    sections: [
      {
        title: "Start with the problem worth solving.",
        text: "We take time to understand your users, business goals, and constraints before recommending an approach. Then we shape a clear product direction and build the right solution—not technology for its own sake.",
      },
      {
        title: "One team from first idea to continued growth.",
        text: "Product strategy, UX and interface design, web and mobile engineering, AI integration, cloud platforms, and ongoing product evolution—all connected by a focus on quality.",
      },
      {
        title: "Built on clarity and collaboration.",
        text: "We work as an extension of your team: making decisions visible, sharing progress early, and keeping delivery focused on outcomes.",
      },
    ],
  },
  careers: {
    eyebrow: "Careers",
    title: "Do thoughtful work. Grow with good people.",
    description:
      "We value curiosity, craftsmanship, and the kind of collaboration that makes ambitious ideas real. Explore what it means to build with Work Bridge India.",
    sections: [
      {
        title: "Solve meaningful problems",
        text: "Help turn real business and customer needs into useful, enduring software.",
      },
      {
        title: "Keep getting better",
        text: "Grow your craft through thoughtful feedback, shared learning, and challenging work.",
      },
      {
        title: "Build together",
        text: "Bring product, design, and engineering perspectives together from the start.",
      },
      {
        title: "A culture built on trust",
        text: "We value ownership, open communication, respect for different perspectives, and the care to do the work well.",
      },
      {
        title: "No open positions right now",
        text: "There are no open roles listed right now. If you care about building thoughtful software, introduce yourself through our contact form.",
      },
    ],
  },
  companies: {
    eyebrow: "How we partner",
    title: "Engineering partnerships built around your goals.",
    description:
      "We partner with founders, product leaders, and established teams to create software that solves the right problem and earns its place in the business.",
    sections: [
      {
        title: "A partnership shaped around your team",
        text: "Whether you need a delivery partner, specialist expertise, or an end-to-end product team, we adapt to your goals and the way you work.",
      },
      {
        title: "From first release to what comes next",
        text: "We can help validate an opportunity, launch a new product, modernize an existing platform, or strengthen the systems your customers rely on.",
      },
    ],
  },
  contact: {
    eyebrow: "Contact",
    title: "Let's build something meaningful.",
    description:
      "Share what you’re working on, what you’re aiming to achieve, and where you need support. Our team will be in touch.",
    sections: [
      {
        title: "Start a conversation",
        text: "A little context helps us make the first conversation useful. Tell us about your goals, timeline, and any challenges you’re navigating.",
      },
      {
        title: "Based in New Delhi",
        text: "Okhla, New Delhi · Delhi NCR",
      },
    ],
  },
  credits: {
    eyebrow: "Credits",
    title: "Built by people who care.",
    description:
      "A look at the product thinking, engineering craft, and open-source tools behind Work Bridge India.",
    sections: [
      {
        title: "Design & product",
        text: "An intentional design system built around clarity, accessibility, and a focused user experience.",
      },
      {
        title: "Engineering",
        text: "A collaborative effort across product, design, and engineering.",
      },
      {
        title: "Open source",
        text: "We’re grateful to the open-source community and the maintainers who make modern product development possible.",
      },
    ],
    tags: ["React", "CSS", "JavaScript"],
  },
  "fraud-alert": {
    eyebrow: "Fraud alert",
    title: "Stay alert. Protect yourself from job scams.",
    description:
      "Fraudsters may pretend to be employers, recruiters, or representatives of legitimate companies. Learn how to recognize common warning signs before sharing information or accepting an opportunity.",
    sections: [
      {
        title: "Never pay someone to get a job",
        text: "Be particularly cautious if someone asks you to send money, purchase gift cards, transfer cryptocurrency, pay a deposit, or purchase equipment as a condition of getting hired.",
      },
      {
        title: "They ask for money",
        text: "Be cautious if someone asks you to pay application fees, training fees, deposits, equipment costs, background-check fees, or other unexpected charges in exchange for a job.",
      },
      {
        title: "They ask for sensitive information",
        text: "Never share passwords, one-time verification codes, banking credentials, or other highly sensitive information with someone claiming to represent an employer.",
      },
      {
        title: "Suspicious messages, links, or attachments",
        text: "Unexpected messages, urgent demands, suspicious links, or requests to continue conversations outside normal hiring channels can be warning signs.",
      },
      {
        title: "Protect yourself",
        text: "Research the company independently, verify the job details, avoid rushed decisions, and report suspicious jobs, accounts, or messages.",
      },
    ],
  },
  grievances: {
    eyebrow: "Feedback & concerns",
    title: "Good partnerships start with listening.",
    description:
      "If something about your experience with Work Bridge India hasn’t met expectations, share your concern with us so we can review it carefully.",
    sections: [
      {
        title: "Tell us what happened",
        text: "Describe the concern, the relevant project or interaction, and what outcome would help address it. Please avoid including passwords or sensitive personal information.",
      },
      {
        title: "What you can raise",
        text: "You can share feedback about our communication, delivery process, project work, or any other part of your experience with our team.",
      },
      {
        title: "What happens next",
        text: "A member of our team will review your message and follow up if we need more context. We aim to respond with a clear next step.",
      },
      {
        title: "Contact our team",
        text: "Use the contact form and select the option that best describes your message.",
      },
    ],
  },
  help: {
    eyebrow: "Project support",
    title: "A few answers to get you started.",
    description:
      "Learn how we work, what to expect from a first conversation, and how to get the right support for your project.",
    sections: [
      {
        title: "What happens in a first conversation?",
        text: "We’ll learn about your goals, users, constraints, and what success means to your team. You can expect a focused discussion—not a one-size-fits-all pitch.",
      },
      {
        title: "Do I need a detailed specification?",
        text: "No. A clear problem or early idea is enough to start. We can help shape requirements, explore options, and identify the right next step.",
      },
      {
        title: "Can you work with our existing team?",
        text: "Yes. We can collaborate with your in-house product, design, and engineering teams, or provide a dedicated team where additional capacity is needed.",
      },
      {
        title: "What technologies do you work with?",
        text: "We work across web and mobile products, cloud infrastructure, data platforms, and practical AI. We recommend technology based on your requirements, not trends.",
      },
      {
        title: "How can I get support?",
        text: "Send us a message through the contact form with your project or support context, and our team will follow up.",
      },
      {
        title: "How do we get started?",
        text: "Share a short overview of what you’re building and the outcome you’re looking for. We’ll take it from there.",
      },
    ],
  },
  "report-issue": {
    eyebrow: "Product support",
    title: "Let’s get your issue to the right team.",
    description:
      "If you’ve found a problem with our website or need help with a project, send us the details and we’ll follow up.",
    sections: [
      {
        title: "Website or accessibility issue",
        text: "Tell us which page or feature you were using, what you expected to happen, and what happened instead.",
      },
      {
        title: "Project or delivery question",
        text: "Include the relevant project context and a good way for our team to reach you.",
      },
      {
        title: "Security or privacy concern",
        text: "Please do not include passwords, access tokens, or sensitive customer data. Share a brief description so we can route your message appropriately.",
      },
      {
        title: "Send a message",
        text: "Use the contact form and choose the closest matching topic. Our team will follow up.",
      },
    ],
  },
  "trust-safety": {
    eyebrow: "Trust & security",
    title: "Responsible engineering, built into the process.",
    description:
      "We treat privacy, security, and reliability as product requirements—not finishing touches.",
    sections: [
      {
        title: "Security considered from day one",
        text: "We consider access, data handling, dependencies, and deployment practices throughout product discovery and implementation.",
      },
      {
        title: "Privacy by design",
        text: "We aim to collect and expose only the information a product needs, with clear attention to how data moves through each system.",
      },
      {
        title: "Reliable by design",
        text: "Thoughtful architecture, testing, observability, and operational practices help products remain dependable as usage grows.",
      },
      {
        title: "Raise a concern",
        text: "If you have a question about security, privacy, or reliability, contact our team with a brief description. Do not include secrets or sensitive data.",
      },
    ],
  },
  news: {
    eyebrow: "Insights",
    title: "Ideas for building better digital products.",
    description:
      "Perspectives on product strategy, software engineering, practical AI, and the decisions that help technology create lasting value.",
    sections: [
      {
        title: "Coming soon",
        text: "We’re preparing useful ideas and perspectives from our product and engineering team. Check back soon.",
      },
    ],
  },
  privacy: {
    eyebrow: "Legal",
    title: "Privacy Policy",
    description:
      "This Privacy Policy explains how Work Bridge India collects, uses, shares, and protects information when you use our platform.",
    lastUpdated: true,
    sections: [
      {
        title: "1. Introduction",
        text: "Work Bridge India (“WBI”, “we”, “us”, or “our”) provides software development, AI, mobile application, and cloud engineering services for businesses. This policy describes how information is handled when you access our website, applications, and related services.",
      },
      {
        title: "2. Information we collect",
        text: "Information you provide may include contact details, account and profile information, professional experience, education, skills, resumes, employment preferences, applications, company details, and communications.",
      },
      {
        title: "3. Automatically collected information",
        text: "Information may include IP address, browser and device information, operating system, pages viewed, approximate location, referring pages, timestamps, and service usage.",
      },
      {
        title: "4. Cookies and similar technologies",
        text: "Cookies may be used to operate the website, remember preferences, maintain sessions, understand usage, and improve services. Choices may depend on your location and applicable law.",
      },
      {
        title: "5. How we use information",
        text: "We use information to provide and improve services, manage accounts, facilitate recruitment, communicate with users, provide support, prevent fraud, maintain security, and comply with legal obligations.",
      },
      {
        title: "6. Applications and profiles",
        text: "Profile or application information may be made available to the relevant employer to facilitate recruitment. Consider carefully what you include in a public or employer-visible profile.",
      },
      {
        title: "7. Employer information",
        text: "Information submitted by employers, including company profiles and job postings, may be displayed publicly or to job seekers depending on platform features and settings. Employers are responsible for ensuring that published information is accurate and that their use of candidate information complies with applicable laws.",
      },
      {
        title: "8. How we share information",
        text: "We may share information with other users when necessary to provide requested services, including application information with the relevant employer. We may also share information with service providers or disclose it when required by law or necessary to protect users, the platform, our rights, or property.",
      },
      {
        title: "9. Service providers",
        text: "We may use third-party providers for infrastructure, authentication, storage, communications, analytics, security, payments, and other operational functions. These providers may process information on our behalf under applicable agreements and laws.",
      },
      {
        title: "10. Data security",
        text: "We use reasonable technical and organizational measures designed to protect information against unauthorized access, loss, misuse, alteration, or disclosure. However, no internet-based service can guarantee complete security.",
      },
      {
        title: "11. Data retention",
        text: "We retain information for as long as reasonably necessary to provide services, maintain business and legal records, resolve disputes, enforce agreements, prevent abuse, and comply with legal obligations. Retention periods may vary by information type and purpose.",
      },
      {
        title: "12. Your choices and rights",
        text: "Depending on where you live and applicable law, you may have rights to access, correct, delete, or obtain a copy of certain information. You may also be able to update information in account settings or request support. Certain rights may be subject to legal exceptions or limitations.",
      },
      {
        title: "13. Account deletion",
        text: "If you want to delete your account, you may use available account settings or contact us for assistance. Some information may need to be retained where required by law or reasonably necessary for legitimate business, security, fraud-prevention, or dispute-resolution purposes.",
      },
      {
        title: "14. Children's privacy",
        text: "Work Bridge India is intended for people legally permitted to use employment and recruitment services. We do not knowingly collect children's personal information in violation of applicable law. Contact us if you believe a child has provided personal information.",
      },
      {
        title: "15. International data transfers",
        text: "Depending on where you live and where service providers operate, your information may be processed or stored in other countries. Where required, we use appropriate safeguards for international data transfers.",
      },
      {
        title: "16. Third-party links",
        text: "Our platform may contain links to third-party websites and services. We are not responsible for their privacy practices and encourage you to review their policies before providing personal information.",
      },
      {
        title: "17. Changes to this Privacy Policy",
        text: "We may update this policy to reflect changes to services, technology, legal requirements, or privacy practices. We will update the last-updated date and, where required by law, provide additional notice.",
      },
      {
        title: "18. Contact us",
        text: `If you have questions about this Privacy Policy or want to exercise a privacy-related right, email ${EMAIL}.`,
      },
    ],
  },
  sitemap: {
    eyebrow: "Sitemap",
    title: "Explore Work Bridge India.",
    description:
      "Explore our capabilities, approach, company information, and resources.",
    sections: [
      {
        title: "Company",
        links: [
          ["Home", "/"],
          ["About us", "/about"],
          ["Careers", "/careers"],
          ["Contact", "/contact"],
          ["Credits", "/credits"],
        ],
      },
      {
        title: "Explore",
        links: [
          ["Jobs", "/jobs"],
          ["Companies", "/companies"],
          ["News", "/news"],
        ],
      },
      {
        title: "Safety & support",
        links: [
          ["Help center", "/help"],
          ["Trust & safety", "/trust-safety"],
          ["Fraud alert", "/fraud-alert"],
          ["Report an issue", "/report-issue"],
          ["Grievances", "/grievances"],
        ],
      },
      {
        title: "Legal",
        links: [
          ["Privacy policy", "/privacy"],
          ["Terms & conditions", "/terms"],
        ],
      },
    ],
  },
  terms: {
    eyebrow: "Legal",
    title: "Terms & Conditions",
    description:
      "These terms explain the rules for using Work Bridge India and the responsibilities of people and organizations using our platform.",
    lastUpdated: true,
    sections: [
      {
        title: "1. About Work Bridge India",
        text: "Work Bridge India (“WBI”, “we”, “us”, or “our”) provides software development, AI, mobile application, and cloud engineering services for businesses. These terms govern use of the website, applications, services, and related features.",
      },
      {
        title: "2. Accepting these terms",
        text: "By accessing or using Work Bridge India, you agree to these terms and our Privacy Policy. If you do not agree, you should not use the platform.",
      },
      {
        title: "3. Accounts",
        text: "You are responsible for providing accurate information, keeping credentials secure, and activity that occurs through your account.",
      },
      {
        title: "4. Job seekers",
        text: "Job seekers are responsible for ensuring that profile, resume, and application information is accurate. We do not guarantee employment, interviews, employer responses, or job availability.",
      },
      {
        title: "5. Employers",
        text: "Employers are responsible for accurate, lawful, and non-misleading job postings and for complying with applicable laws when using candidate information.",
      },
      {
        title: "6. User content",
        text: "Users remain responsible for content they submit and must have the rights and permissions to provide it. Content that violates these terms, applicable law, or safety policies may be restricted or removed.",
      },
      {
        title: "7. Acceptable use",
        text: "Do not commit fraud, impersonate others, post fake jobs, harass or discriminate against users, gain unauthorized access, collect information without permission, or use the platform unlawfully.",
      },
      {
        title: "8. Trust & safety",
        text: "We work to maintain a safe environment, but cannot guarantee every user, employer, job listing, or piece of information is legitimate or accurate. Report suspicious activity to us.",
      },
      {
        title: "9. Intellectual property",
        text: "The platform, including branding, design, software, graphics, and original materials, is owned by or licensed to Work Bridge India and may be protected by intellectual property laws.",
      },
      {
        title: "10. Privacy",
        text: "Your use of Work Bridge India is also subject to our Privacy Policy, which explains how we collect, use, store, and protect personal information.",
      },
      {
        title: "11. Third-party services and links",
        text: "Third-party websites, services, and resources have their own terms and policies. We are not responsible for their availability, content, or practices.",
      },
      {
        title: "12. Availability of the platform",
        text: "We work to keep the platform available and reliable, but do not guarantee uninterrupted, secure, or error-free access. We may modify, suspend, or discontinue features or services.",
      },
      {
        title: "13. Account suspension or termination",
        text: "We may restrict, suspend, or terminate an account or access if we reasonably believe a user has violated these terms, applicable law, or safety policies. You may stop using the platform and request account closure through available procedures.",
      },
      {
        title: "14. Disclaimers",
        text: "Work Bridge India provides a platform for connecting job seekers and employers. We do not guarantee user-provided information, job availability, or that a job will result in employment.",
      },
      {
        title: "15. Limitation of liability",
        text: "To the extent permitted by applicable law, Work Bridge India and its affiliates, employees, and service providers will not be responsible for indirect, incidental, special, consequential, or similar losses arising from platform use or inability to use the platform.",
      },
      {
        title: "16. Changes to these terms",
        text: "We may update these terms to reflect changes to services, legal requirements, or business practices. We will update the last-updated date. Continued use after an update means acceptance of the revised terms, subject to applicable law.",
      },
      {
        title: "17. Contact us",
        text: `For questions about these terms or your account, email ${EMAIL}.`,
      },
    ],
  },
};

const navItems = [
  ["Services", "/#services"],
  ["Our approach", "/#approach"],
  ["Technology", "/#technology"],
  ["About", "/about"],
  ["Contact", "/contact"],
];

function usePathname() {
  const [pathname, setPathname] = useState(window.location.pathname);

  useEffect(() => {
    const handlePopState = () => setPathname(window.location.pathname);
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  return pathname.replace(/\/+$/, "") || "/";
}

function InternalLink({ href, onClick, children, ...props }) {
  return (
    <a
      href={href}
      {...props}
      onClick={(event) => {
        onClick?.(event);
        if (
          event.defaultPrevented ||
          event.metaKey ||
          event.ctrlKey ||
          event.shiftKey ||
          event.altKey
        ) {
          return;
        }
        const destination = new URL(href, window.location.href);
        if (destination.origin !== window.location.origin) return;
        event.preventDefault();
        window.history.pushState({}, "", destination);
        window.dispatchEvent(new PopStateEvent("popstate"));
        if (destination.hash) {
          window.requestAnimationFrame(() =>
            document.getElementById(destination.hash.slice(1))?.scrollIntoView(),
          );
        } else {
          window.scrollTo(0, 0);
        }
      }}
    >
      {children}
    </a>
  );
}

function Header() {
  const [isHidden, setIsHidden] = useState(false);

  useEffect(() => {
    let previousScrollY = window.scrollY;
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setIsHidden(currentScrollY > 80 && currentScrollY > previousScrollY);
      previousScrollY = currentScrollY;
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <header className={`site-header${isHidden ? " site-header--hidden" : ""}`}>
      <InternalLink className="brand" href="/">
        <img
          className="brand-logo"
          src="https://res.cloudinary.com/dflhcbyo/image/upload/v1790430841/w.png"
          alt=""
          width="42"
          height="42"
        />
        <span>Work Bridge India</span>
      </InternalLink>
      <nav aria-label="Main navigation">
        {navItems.map(([label, href]) => (
          <InternalLink key={label} href={href}>{label}</InternalLink>
        ))}
        <InternalLink className="button button-dark" href="/contact">
          Start a project
        </InternalLink>
      </nav>
    </header>
  );
}

function Footer() {
  const groups = [
    { title: "Explore", links: [["Services", "/#services"], ["Our approach", "/#approach"], ["Technology", "/#technology"], ["About us", "/about"]] },
    { title: "Company", links: [["Careers", "/careers"], ["Contact", "/contact"], ["Help center", "/help"], ["Report an issue", "/report-issue"]] },
    { title: "Legal", links: [["Privacy policy", "/privacy"], ["Terms & conditions", "/terms"], ["Trust & security", "/trust-safety"], ["Sitemap", "/sitemap"]] },
  ];

  return (
    <footer className="site-footer">
      <div className="page-wrap footer-content">
        <div className="footer-brand">
          <InternalLink href="/" className="footer-logo">Work Bridge India</InternalLink>
          <p>We design and engineer digital products that help ambitious businesses move forward.</p>
          <p>Okhla, New Delhi · Delhi NCR</p>
        </div>
        <div className="footer-links">
          {groups.map((group) => (
            <div key={group.title}>
              <h2>{group.title}</h2>
              {group.links.map(([label, href]) => (
                <InternalLink key={href} href={href}>{label}</InternalLink>
              ))}
            </div>
          ))}
        </div>
        <div className="footer-bottom">
          <p>© {new Date().getFullYear()} Work Bridge India. All rights reserved.</p>
          <InternalLink href="/sitemap">Explore all public pages</InternalLink>
        </div>
      </div>
    </footer>
  );
}

function HomePage() {
  return (
    <>
      <section className="landing-hero page-wrap">
        <div className="landing-copy">
          <p className="eyebrow">Product strategy · Engineering · AI</p>
          <h1>Software built to move <em>business forward.</em></h1>
          <p className="landing-lede">
            We partner with ambitious teams to turn complex challenges into clear, dependable digital products—designed for people and engineered for growth.
          </p>
          <div className="actions">
            <InternalLink className="button button-dark" href="/contact">Discuss your project <span>↗</span></InternalLink>
            <InternalLink className="text-link" href="#services">Explore our capabilities <span>↓</span></InternalLink>
          </div>
          <div className="hero-proof">
            <div className="proof-mark" aria-hidden="true"><span>W</span></div>
            <p><strong>One partner, from idea to impact</strong><br />Product thinking and engineering in sync</p>
          </div>
        </div>
        <div className="landing-visual">
          <img
            src="https://res.cloudinary.com/dflhcbyo/image/upload/v1790424397/ant-rozetsky-HXOllTSwrpM-unsplash.jpg"
            alt="A bright, collaborative workspace for building great software"
          />
          <div className="visual-note">
            <span className="status-dot">From strategy to scale</span>
            <strong>Make progress real.</strong>
            <small>Clear thinking. Thoughtful delivery.</small>
          </div>
        </div>
      </section>

      <div className="landing-ribbon">
        <div className="page-wrap ribbon-inner"><span>DISCOVER</span><i>✳</i><span>DESIGN</span><i>✳</i><span>ENGINEER</span><i>✳</i><span>GROW</span></div>
      </div>

      <section className="landing-section page-wrap landing-intro tech-partner" id="approach">
        <div className="tech-partner-heading">
          <p className="eyebrow">A partner in progress</p>
          <h2>Make your next move with <em>confidence.</em></h2>
        </div>
        <div className="tech-partner-content">
          <p className="section-lede">The right technology starts with a clear understanding of the people and business it should serve. We work alongside your team to find the opportunity, shape the product, and deliver a dependable solution that can evolve with you.</p>
          <ul className="tech-partner-points"><li>Product decisions grounded in real needs</li><li>Design and engineering working as one</li><li>Built to adapt as your business grows</li></ul>
          <InternalLink className="arrow-link" href="/contact">Discuss your project <span>↗</span></InternalLink>
        </div>
      </section>

      <section className="landing-section page-wrap service-section" id="services">
        <div className="section-topline">
          <div><p className="eyebrow">How we help</p><h2>Everything you need to bring a better product to life.</h2></div>
          <span>01 — 03</span>
        </div>
        <div className="service-grid">
          <article className="service-card service-dark"><div className="service-index">01 / PRODUCT ENGINEERING</div><div><h3>Digital products</h3><p>We design and build web platforms and business software around real user needs and measurable outcomes.</p><InternalLink className="arrow-link" href="/contact">Build a better product <span>↗</span></InternalLink></div></article>
          <article className="service-card service-lime"><div className="service-index">02 / AI &amp; AUTOMATION</div><div><h3>Applied AI</h3><p>Bring intelligent features and streamlined workflows into your business with thoughtful, human-centered AI.</p><InternalLink className="arrow-link" href="/contact">Put AI to work <span>↗</span></InternalLink></div></article>
          <article className="service-card service-dark"><div className="service-index">03 / MOBILE &amp; CLOUD</div><div><h3>Connected systems</h3><p>Create polished mobile experiences and resilient cloud foundations that work together seamlessly.</p><InternalLink className="arrow-link" href="/contact">Plan your platform <span>↗</span></InternalLink></div></article>
        </div>
      </section>

      <section className="delivery-section">
        <div className="page-wrap">
          <div className="section-topline"><div><p className="eyebrow">A considered way to build</p><h2>From the first question to lasting value.</h2></div><p className="tech-intro">A clear, collaborative process keeps people aligned and progress visible at every stage.</p></div>
          <ol className="delivery-grid">
            <li className="delivery-card"><span className="delivery-number">01</span><h3>Discover</h3><p>We align on your users, business goals, constraints, and the opportunity worth pursuing.</p></li>
            <li className="delivery-card"><span className="delivery-number">02</span><h3>Shape</h3><p>We turn what we learn into a focused product direction, practical scope, and delivery plan.</p></li>
            <li className="delivery-card"><span className="delivery-number">03</span><h3>Deliver</h3><p>We build in thoughtful iterations, sharing working progress and learning along the way.</p></li>
            <li className="delivery-card"><span className="delivery-number">04</span><h3>Evolve</h3><p>We help you improve, support, and scale your product as your needs change.</p></li>
          </ol>
        </div>
      </section>

      <section className="landing-section page-wrap tech-section" id="technology">
        <div className="section-topline"><div><p className="eyebrow">Technology, thoughtfully applied</p><h2>The right tools for the job—and the journey ahead.</h2></div><p className="tech-intro">We choose technologies for their fit, longevity, and ability to work well together—creating systems that are maintainable, secure, and ready to grow.</p></div>
        <div className="tech-groups">
          {technologyGroups.map((group) => (
            <article key={group.title} className={`tech-group${group.number === "05" ? " tech-group-ai" : ""}`}>
              <div className="tech-group-heading"><span className="tech-group-number">{group.number}</span><h3>{group.title}</h3></div>
              <p>{group.description}</p>
              <ul>{group.technologies.map((technology) => <li key={technology}>{technology}</li>)}</ul>
            </article>
          ))}
        </div>
        <div className="tech-section-footer"><p>Technology should serve the product—not the other way around.</p><InternalLink className="arrow-link" href="/contact">Talk through your goals <span>↗</span></InternalLink></div>
      </section>

      <section className="partnership-section page-wrap">
        <div className="partnership-copy"><p className="eyebrow">A long-term perspective</p><h2>Good software gets better with every step.</h2><p>We care about what happens after launch. Thoughtful foundations, reliable delivery, and ongoing collaboration help your product keep pace with your customers and your business.</p><InternalLink className="arrow-link" href="/about">Meet your technology partner <span>↗</span></InternalLink></div>
        <div className="partnership-list" aria-label="How we support your product">
          <article><span>01</span><div><h3>Performance by design</h3><p>Fast, responsive experiences built around real users.</p></div></article>
          <article><span>02</span><div><h3>Scale without the rebuild</h3><p>Practical foundations that can evolve with your workload.</p></div></article>
          <article><span>03</span><div><h3>Security in the details</h3><p>Thoughtful engineering from the first design decision.</p></div></article>
        </div>
      </section>

      <section className="landing-cta"><div className="page-wrap cta-inner"><p className="eyebrow">Have a challenge in mind?</p><h2>Let’s make your next move <em>count.</em></h2><p className="cta-description">Bring us the problem. We’ll help you find a thoughtful way forward.</p><InternalLink className="button button-dark" href="/contact">Start a conversation <span>↗</span></InternalLink></div></section>
    </>
  );
}

function PageContent({ page }) {
  const mailto = `mailto:${EMAIL}`;
  return (
    <main>
      <section className="route-hero"><div className="page-wrap"><p className="eyebrow">{page.eyebrow}</p><h1>{page.title}</h1><p>{page.description}</p>{page.lastUpdated && <small>Last updated: September 26, 2026</small>}</div></section>
      <div className="page-wrap route-content">
        {page.sections?.map((section) => (
          <section className="route-card" key={section.title}>
            <h2>{section.title}</h2>
            {section.text && <p>{section.text}</p>}
            {section.links && <ul className="route-links">{section.links.map(([label, href]) => <li key={href}><InternalLink href={href}>{label}<span>↗</span></InternalLink></li>)}</ul>}
            {/contact|send a report/i.test(section.title) ? <a className="button button-dark" href={mailto}>Email our team</a> : null}
          </section>
        ))}
        {page.eyebrow === "Contact" && <ContactForm />}
        {page.tags && <div className="technology-tags">{page.tags.map((tag) => <span key={tag}>{tag}</span>)}</div>}
        {page.eyebrow === "Careers" && <div className="route-cta"><p>Don’t see the right role? Tell us how you could contribute.</p><a className="arrow-link" href={mailto}>Email our team <span>↗</span></a></div>}
      </div>
    </main>
  );
}

function ContactForm() {
  const [state, handleSubmit] = useForm("xwpngrdb");

  if (state.succeeded) {
    return (
      <section className="contact-form-card" aria-live="polite">
        <p className="eyebrow">Message received</p>
        <h2>Thank you for reaching out.</h2>
        <p>Your message has been sent. We appreciate your interest in Work Bridge India.</p>
      </section>
    );
  }

  return (
    <form className="contact-form-card" onSubmit={handleSubmit}>
      <p className="eyebrow">Contact us</p>
      <h2>Tell us about your project.</h2>
      <p className="contact-form-intro">Complete the form and our team will get back to you.</p>

      <div className="contact-form-grid">
        <label>
          Name
          <input autoComplete="name" name="name" required />
        </label>
        <label>
          Email
          <input autoComplete="email" name="email" type="email" required />
        </label>
        <label className="contact-form-full">
          What can we help with?
          <select name="topic" defaultValue="" required>
            <option value="" disabled>Select a topic</option>
            <option value="Software development">Software development</option>
            <option value="AI and automation">AI and automation</option>
            <option value="Mobile apps">Mobile apps</option>
            <option value="Cloud and DevOps">Cloud and DevOps</option>
            <option value="Other">Other</option>
          </select>
        </label>
        <label className="contact-form-full">
          Subject
          <input name="subject" required />
        </label>
        <label className="contact-form-full">
          Message
          <textarea name="message" rows="6" required />
        </label>
      </div>

      {state.errors && (
        <p className="contact-form-error" role="alert">
          We couldn&apos;t send your message. Please try again in a moment.
        </p>
      )}

      <button className="button button-dark" type="submit" disabled={state.submitting}>
        {state.submitting ? "Sending..." : "Send message"}
        {!state.submitting && <span aria-hidden="true"> ↗</span>}
      </button>
    </form>
  );
}

function JobsPage({ jobId }) {
  if (jobId) {
    return <PageContent page={{
      eyebrow: "Career opportunity",
      title: "Build thoughtful software with us.",
      description: `Role reference: ${decodeURIComponent(jobId)}.`,
      sections: [{ title: "Role details", text: "This presentation does not publish active vacancies. Visit our Careers page for current information about joining the team." }],
    }} />;
  }
  return <PageContent page={{
    eyebrow: "Careers",
    title: "Find meaningful work in product and engineering.",
    description: "Join a curious, collaborative team building software that helps businesses move forward.",
    sections: [{ title: "No current openings", text: "We don’t have active roles listed right now. Visit our Careers page to learn about our team and how to get in touch." }],
  }} />;
}

function CompaniesPage({ companyId }) {
  if (companyId) {
    return <PageContent page={{
      eyebrow: "Company profile",
      title: "A closer look at our partnerships.",
      description: `Partnership reference: ${decodeURIComponent(companyId)}.`,
      sections: [{ title: "Partnership details", text: "Every engagement is shaped around a team’s goals, users, and technical context. Contact us to discuss the kind of partnership that would help your business." }],
    }} />;
  }
  return <PageContent page={pages.companies} />;
}

function NotFound() {
  return <PageContent page={{ eyebrow: "Page not found", title: "We couldn't find that page.", description: "The address may have changed, or the page may no longer be available.", sections: [{ title: "Explore Work Bridge India", links: [["Home", "/"], ["About", "/about"], ["Sitemap", "/sitemap"]] }] }} />;
}

function App() {
  const pathname = usePathname();
  const segments = pathname.split("/").filter(Boolean);
  const pageKey = segments[0] || "";
  const page = pages[pageKey];
  const knownRoutes = new Set(["", "about", "careers", "companies", "contact", "credits", "fraud-alert", "grievances", "help", "jobs", "news", "privacy", "report-issue", "sitemap", "terms", "trust-safety"]);

  useEffect(() => {
    const title = page?.title || (pageKey === "jobs" ? "Find work worth doing." : "Work Bridge India | Software, AI & Product Engineering");
    document.title = `${title} | Work Bridge India`;
  }, [page, pageKey]);

  return (
    <div className="app">
      <Header />
      {pathname === "/" ? <HomePage /> :
        pageKey === "jobs" ? <JobsPage jobId={segments[1]} /> :
          pageKey === "companies" ? <CompaniesPage companyId={segments[1]} /> :
            page && knownRoutes.has(pageKey) ? <PageContent page={page} /> : <NotFound />}
      <Footer />
    </div>
  );
}

export default App;
